import { createTheme, Theme } from "@mui/material/styles";

/**
 * Selectable brand palettes. Keys (green/greenDark/greenDarker/greenSoft/...)
 * are kept stable so every component that reads BRAND.* keeps working — only
 * the values change when the user picks a different colour.
 */
export interface BrandTokens {
  green: string;        // primary action / accents
  greenDark: string;    // distributor sidebar
  greenDarker: string;
  greenSoft: string;    // soft fills, selected nav
  primaryDark: string;  // primary.dark (hover)
  surface: string;
  border: string;
  text: string;
  textMuted: string;
}

const NEUTRAL = { surface: "#f5f7f6", border: "#e6e9e7", text: "#1c2a22", textMuted: "#6b7a72" };

export const PALETTES: Record<string, BrandTokens> = {
  green:  { green: "#1f7a4d", greenDark: "#14432f", greenDarker: "#0f3324", greenSoft: "#e7f3ec", primaryDark: "#176039", ...NEUTRAL },
  blue:   { green: "#1f6fb0", greenDark: "#123a5e", greenDarker: "#0d2c47", greenSoft: "#e8f1fa", primaryDark: "#175a92", ...NEUTRAL },
  indigo: { green: "#5a3fc0", greenDark: "#2e2156", greenDarker: "#241a44", greenSoft: "#efeafb", primaryDark: "#4a33a0", ...NEUTRAL },
  teal:   { green: "#0f8a8a", greenDark: "#0a3f3f", greenDarker: "#072f2f", greenSoft: "#e4f3f3", primaryDark: "#0b6f6f", ...NEUTRAL },
  orange: { green: "#e07b1a", greenDark: "#5a3410", greenDarker: "#42270c", greenSoft: "#fcefe0", primaryDark: "#c0680f", ...NEUTRAL },
};

export const PALETTE_KEYS = Object.keys(PALETTES);
export const PALETTE_LABELS: Record<string, string> = {
  green: "Green", blue: "Blue", indigo: "Indigo", teal: "Teal", orange: "Orange",
};

export const DEFAULT_PALETTE = "green";

/** Live, mutable brand object that components import. Updated by applyBrand(). */
export const BRAND: BrandTokens = { ...PALETTES[DEFAULT_PALETTE] };

/** Overwrite BRAND in place with the chosen palette (keeps the same reference). */
export function applyBrand(key: string): void {
  Object.assign(BRAND, PALETTES[key] || PALETTES[DEFAULT_PALETTE]);
}

/** Build a MUI theme for a palette key. */
export function buildTheme(key: string): Theme {
  const b = PALETTES[key] || PALETTES[DEFAULT_PALETTE];
  return createTheme({
    palette: {
      mode: "light",
      primary: { main: b.green, dark: b.primaryDark, contrastText: "#ffffff" },
      success: { main: "#2e9e5b" },
      warning: { main: "#e2920f" },
      info: { main: "#2f6fb0" },
      background: { default: b.surface, paper: "#ffffff" },
      text: { primary: b.text, secondary: b.textMuted },
      divider: b.border,
    },
    shape: { borderRadius: 10 },
    typography: {
      fontFamily: '"Inter","Segoe UI",system-ui,-apple-system,"Helvetica Neue",Arial,sans-serif',
      h4: { fontWeight: 700 }, h5: { fontWeight: 700 }, h6: { fontWeight: 700 },
      subtitle2: { fontWeight: 600 }, button: { textTransform: "none", fontWeight: 600 },
    },
    components: {
      MuiPaper: { styleOverrides: { root: { backgroundImage: "none" }, outlined: { borderColor: b.border } } },
      MuiButton: { defaultProps: { disableElevation: true } },
      MuiCard: { defaultProps: { variant: "outlined" }, styleOverrides: { root: { borderColor: b.border } } },
      MuiChip: { styleOverrides: { root: { fontWeight: 600 } } },
    },
  });
}

// Default (green) theme for any remaining static import.
const theme = buildTheme(DEFAULT_PALETTE);
export default theme;
